import React, { useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import { MLDriverVehicleUtil ,MLNotificationBlockUtil} from 'ml-portals-util-react';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import appConfig from 'app-config';

import styles from './MLDriverChangeDisclosureComponent.module.scss';
import metadata from './MLDriverChangeDisclosureComponent.metadata.json5';

import { IntlContext } from '@jutro/legacy/locale';

const MLDriverChangeDisclosureComponent = (props) => {
    const {
        submissionVM,
        driver,
        isPUP,
        refreshData,
        onValidate,
        showValidationErrors,
        isExcluded
    } = props;

    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    
    const [showDisclosureValidtionErrors, setShowDisclosureValidationErrors] = useState(false);
    const [isAddDisclosureVisible, setIsAddDisclosureVisible] = useState(false);
    const [maxClaimDate, setMaxClaimDate] = useState({});
    const [disclosureVM, setDisclosureVM] = useState({});
    const INCIDENT_CATEGORY_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('IncidentCategory_ML');
    const INCIDENT_DESCRIPTION_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('IncidentDescription_ML');
    const disclosurePath = isPUP ? 'disclosures' : 'disclosures_ML';
    const isGPA = appConfig.portalCode === 'GPA';

    const {
        getFullName,
    } = MLDriverVehicleUtil();
    const {
        addErrorAlert,
        clearErrorAlerts
    } = MLNotificationBlockUtil();

    const intl = useContext(IntlContext);
    const getFormattedDateString = useCallback((gwDateVal) => {
        const date = LocalDateUtil.toMidnightDate(gwDateVal);
        return intl.formatDate(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
    }, [intl]);
    const showDuplicateDisclosureAlert = useCallback(() => {
        addErrorAlert('Activity already exists with matching details.', 'duplicateDisclosureAlert');
    }, [addErrorAlert]);

    const setEditingDriver = (p) => {
        p.currentDriverVM = driver;
        p.currentDriverVM[disclosurePath].value = p.currentDriverVM[disclosurePath].value || [];
        return p;
    };

    const initDisclosures = (p) => {
        if (!p.currentDriverVM[disclosurePath].value || p.currentDriverVM[disclosurePath].value.length === 0) {
            p.currentDriverVM[disclosurePath].value = { accidents: [], claims: [], violations: [] };
        } else {
            if (p.currentDriverVM[disclosurePath].accidents.value.length === 0) {
                p.currentDriverVM[disclosurePath].accidents.value = [];
            } else {
                p.allDisclosures.unshift(...p.currentDriverVM[disclosurePath].accidents.value);
            }
            if (p.currentDriverVM[disclosurePath].claims.value.length === 0) {
                p.currentDriverVM[disclosurePath].claims.value = [];
            } else {
                p.allDisclosures.unshift(...p.currentDriverVM[disclosurePath].claims.value);
            }
            if (p.currentDriverVM[disclosurePath].violations.value.length === 0) {
                p.currentDriverVM[disclosurePath].violations.value = [];
            } else {
                p.allDisclosures.unshift(...p.currentDriverVM[disclosurePath].violations.value);
            }
        }
    };

    const initPageState = () => {
        const p = {
            allDisclosures: []
        };
        setEditingDriver(p);
        initDisclosures(p);
        return p;
    };

    const [page, setPage] = useState(initPageState);

    const createNewDisclosure = (discCategory) => {
        const newDisclosureFields = {
            source: 'customer_disclosed'
        };
        const DISCLOSURE_TYPES = {
            ACCIDENT: INCIDENT_CATEGORY_TYPELIST.getCode('accident'),
            CLAIM: INCIDENT_CATEGORY_TYPELIST.getCode('other_claim'),
            VIOLATION: INCIDENT_CATEGORY_TYPELIST.getCode('violation')
        };
        let newDisclosure;
        switch (discCategory) {
            case (DISCLOSURE_TYPES.ACCIDENT.code):
                newDisclosure = viewModelService.create(newDisclosureFields, 'pc',
                    'metlife.edge.capabilities.policycommon.disclosures.dto.AccidentDTOML');
                newDisclosure.date = disclosureVM.date.value;
                newDisclosure.paidAmount = undefined
                break;
            case (DISCLOSURE_TYPES.CLAIM.code):
                newDisclosure = viewModelService.create(newDisclosureFields, 'pc',
                    'metlife.edge.capabilities.policycommon.disclosures.dto.ClaimDTOML');
                newDisclosure.date = disclosureVM.date.value;
                newDisclosure.paidAmount = undefined
                break;
            case (DISCLOSURE_TYPES.VIOLATION.code):
                newDisclosure = viewModelService.create(newDisclosureFields, 'pc',
                    'metlife.edge.capabilities.policycommon.disclosures.dto.ViolationDTOML');
                newDisclosure.date = disclosureVM.date.value;
                newDisclosure.state = submissionVM.baseData.policyAddress.state.value;
                break;
            default:
                newDisclosure = viewModelService.create(newDisclosureFields, 'pc',
                    'metlife.edge.capabilities.policycommon.disclosures.dto.AccidentDTOML');
                newDisclosure.paidAmount = undefined
                newDisclosure.date = undefined;
                discCategory = undefined;
                break;
        }
        newDisclosure.category = discCategory;
        newDisclosure.person = page.currentDriverVM[isPUP ? 'contact' : 'person'].value;
        newDisclosure.value.isCategoryRequired = true;
        return newDisclosure;
    };

    const onDisclosureCategoryChange = (discCategoryChange) => {
        const previousDate = disclosureVM.date.value;
        setDisclosureVM(createNewDisclosure(discCategoryChange));

        disclosureVM.date.value = previousDate;
    };

    const hasAnyPriorDisclosures = () => {
        return page.allDisclosures.length > 0;
    };

    const hasPrefilledDisclosures = () => {
        return page.allDisclosures.some((d) => d.source !== 'customer_disclosed');
    };

    const hasManuallyEnteredDisclosures = () => {
        return hasAnyPriorDisclosures() && page.allDisclosures.some((d) => d.source === 'customer_disclosed');
    };

    const isCustomerDisclosure = (disc) => {
        return disc.source === 'customer_disclosed';
    };

    const removePriorDisclosure = (evt) => {
        let idx = evt.index;

        const disc = page.allDisclosures[idx];
        page.allDisclosures.splice(idx, 1);

        switch (disc.category) {
            case ('accident'):
                idx = disc.publicID ? page.currentDriverVM[disclosurePath].accidents.value.findIndex((accident) => accident.publicID === disc.publicID) : page.currentDriverVM[disclosurePath].accidents.value.indexOf(disc);
                if (idx > -1) {
                    page.currentDriverVM[disclosurePath].accidents.value.splice(idx, 1);
                }
                break;
            case ('other_claim'):
                idx = disc.publicID ? page.currentDriverVM[disclosurePath].claims.value.findIndex((claim) => claim.publicID === disc.publicID) : page.currentDriverVM[disclosurePath].claims.value.indexOf(disc);
                if (idx > -1) {
                    page.currentDriverVM[disclosurePath].claims.value.splice(idx, 1);
                }
                break;
            case ('violation'):
                idx = disc.publicID ? page.currentDriverVM[disclosurePath].violations.value.findIndex((violation) => violation.publicID === disc.publicID) : page.currentDriverVM[disclosurePath].violations.value.indexOf(disc);
                if (idx > -1) {
                    page.currentDriverVM[disclosurePath].violations.value.splice(idx, 1);
                }
                break;
            default:
                break;
        }

        setPage({ ...page });
    };

    const showAddDisclosureSection = () => {
        setMaxClaimDate(MLLocalDateUtil.date2LocalDate(new Date()));
        maxClaimDate.day -= 1;
        setDisclosureVM(createNewDisclosure());
        setIsAddDisclosureVisible(true);
    };

    const hideAddDisclosureSection = () => {
        setIsAddDisclosureVisible(false);
        setShowDisclosureValidationErrors(false);
    };

    const finishAddingDisclosure = () => {
        if (disclosureVM.aspects.valid && disclosureVM.aspects.subtreeValid) {
            if (disclosureVM.category && disclosureVM.category.value && disclosureVM.category.value.code === 'violation') {
                disclosureVM.value.paidAmount = undefined;
            }
            if (disclosureVM.paidAmount && disclosureVM.paidAmount.value) {
                disclosureVM.paidAmount.currency.value = 'usd';
            }
            const disclosure = disclosureVM.value;
            const disclosureCheck = _.filter(page?.allDisclosures , (item) => item?.type === disclosure?.type && item?.paidAmount?.amount === disclosure?.paidAmount?.amount && item?.person?.publicID === disclosure?.person?.publicID
            && item?.date?.day === disclosure?.date?.day && item?.date?.month === disclosure?.date?.month && item?.date?.year === disclosure?.date?.year)
            if(disclosureCheck?.length <1) {
                clearErrorAlerts();
            switch (disclosureVM.value.category) {
                case ('accident'):
                    page.currentDriverVM[disclosurePath].accidents.value.unshift(disclosureVM.value);
                    break;
                case ('other_claim'):
                    page.currentDriverVM[disclosurePath].claims.value.unshift(disclosureVM.value);
                    break;
                case ('violation'):
                    page.currentDriverVM[disclosurePath].violations.value.unshift(disclosureVM.value);
                    break;
                default:
                    break;
            }
            page.allDisclosures.unshift(disclosureVM.value);
            hideAddDisclosureSection();
            }else{
                clearErrorAlerts();
                showDuplicateDisclosureAlert();
            }
        } else {
            setShowDisclosureValidationErrors(true);
        }
    };

    const formatAvailableValues = (unmappedValues) => {
        return unmappedValues.map((availableValue) => ({
            code: availableValue.code,
            name: translator({
                id: availableValue.name,
                defaultMessage: availableValue.name
            })
        }));
    };

    const getCategoryValues = (viewModel) => {
        if (!viewModel) return [];
        return formatAvailableValues(viewModel.aspects.availableValues);
    };

    const getTypeValues = (category) => {
        if (!category || !category.value || !category.value.code) return [];
        const categoryName = category.value.code === 'other_claim' ? 'Claim' : _.startCase(_.toLower(category.value.code));
        const filteredTypes = INCIDENT_DESCRIPTION_TYPELIST.getFilter(categoryName);
        return formatAvailableValues(filteredTypes.codes);
    };

    const onAmountValueChange = (value, path) => {
        if (value.amount === "" || value.amount === null || value.amount === undefined) {
            // it should be paidAmount:undefined rather than paidAmount:{amount:undefined,curreny;us} onValueChange to show feild level error for currency Component
            _.set(disclosureVM, path, undefined);
        } else {
            _.set(disclosureVM, path, value);
        }

        setDisclosureVM(disclosureVM);
        refreshData();
    }

    const generateAddDisclosureOverrides = () => {
        const baseHeaderClass = 'subsection-header vertical-spacing-20';
        const baseTableClass = 'stack-table standard-table';

        return {
            showExcludedDisclosureDescription: {
                visible: isExcluded
            },
            allDisclosuresIterable: {
                data: page.allDisclosures
            },
            allDisclosuresTableContainer: {
                visible: hasAnyPriorDisclosures()
            },
            disclosureTable: {
                className: isGPA ? classNames(baseTableClass) : classNames(baseTableClass, 'disclosure-table-overflow')
            },
            showPriorDisclosure: {
                visible: hasAnyPriorDisclosures()
            },
            showPrefilledDisclosureDescription: {
                visible: hasPrefilledDisclosures()
            },
            showManualDisclosureDescription: {
                visible: !hasPrefilledDisclosures() && hasManuallyEnteredDisclosures(),
                className: isGPA ? classNames(baseHeaderClass, 'gpa-header') : baseHeaderClass
            },
            showNoDisclosureDescription: {
                visible: !hasPrefilledDisclosures() && !hasManuallyEnteredDisclosures()
            },
            showDisclosureButton: {
                visible: !isAddDisclosureVisible
            },
            addDisclosureTopContainer: {
                visible: isAddDisclosureVisible
            },
            addDisclosureDateOfEvent: {
                showErrors: showDisclosureValidtionErrors
            },
            addDisclosureCategory: {
                availableValues: getCategoryValues(disclosureVM.category),
                onValueChange: onDisclosureCategoryChange,
                showErrors: showDisclosureValidtionErrors
            },
            addDisclosureType: {
                availableValues: getTypeValues(disclosureVM.category),
                showErrors: showDisclosureValidtionErrors
            },
            addDisclosureAmountPaid: {
                visible: !!disclosureVM.category && !!disclosureVM.category.value && ['other_claim', 'accident'].includes(disclosureVM.category.value.code),
                showFractions: true,
                showErrors: showDisclosureValidtionErrors,
                onValueChange: onAmountValueChange
            }
        };
    };

    const generateAllDisclosureOverrides = () => {
        const getTypeKeyDescription = (typeKeyName) => {
            return translator({
                id: typeKeyName,
                defaultMessage: typeKeyName
            });
        };

        const overrides = page.allDisclosures.map((disc, index) => {
            return {
                [`dateCell${index}`]: {
                    content: getFormattedDateString(disc.date)
                },
                [`customerDisclosedCell${index}`]: {
                    visible: isCustomerDisclosure(disc)
                },
                [`driverCell${index}`]: {
                    content: getFullName(disc.person)
                },
                [`categoryCell${index}`]: {
                    content: getTypeKeyDescription(INCIDENT_CATEGORY_TYPELIST.getCode(disc.category).name)
                },
                [`typeCell${index}`]: {
                    content: getTypeKeyDescription(INCIDENT_DESCRIPTION_TYPELIST.getCode(disc.type).name)
                },
                [`amountPaidCell${index}`]: {
                    content: disc.paidAmount ? `$${parseFloat(disc.paidAmount.amount).toFixed(2)}` : ''
                },
                [`actionCell${index}`]: {
                    visible: isCustomerDisclosure(disc)
                }
            };
        });

        return Object.assign({}, ...overrides);
    };

    const overrideProps = {
        ...generateAddDisclosureOverrides(),
        ...generateAllDisclosureOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            showAddDisclosureSection: showAddDisclosureSection,
            finishAddingDisclosure: finishAddingDisclosure,
            hideAddDisclosureSection: hideAddDisclosureSection,
            onDisclosureCategoryChange: onDisclosureCategoryChange,
            removePriorDisclosure: removePriorDisclosure
        }
    };
    
    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={disclosureVM}
            overrideProps={overrideProps}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            onModelChange={refreshData}
            onValidationChange={onValidate}
            showErrors={showDisclosureValidtionErrors}
        />
    );
};

MLDriverChangeDisclosureComponent.propTypes = {
    submissionVM: PropTypes.object.isRequired,
    driver: PropTypes.object.isRequired,
    refreshData: PropTypes.func.isRequired,
    onValidate: PropTypes.func.isRequired,
    showValidationErrors: PropTypes.bool,
    isExcluded: PropTypes.bool
}

MLDriverChangeDisclosureComponent.defaultProps = {
    showValidationErrors: false,
    isExcluded: false
}

export default MLDriverChangeDisclosureComponent;
